import { ReactNode } from 'react';
import { useAuth } from '@/shared/hooks/useAuth';
import { useLocation } from 'wouter';
import { useEffect } from 'react';
import PageLoader from '@/shared/components/PageLoader';

interface AuthGuardProps {
  children: ReactNode;
  fallbackPath?: string;
}

/**
 * Authentication guard component that protects routes requiring user login
 * Redirects unauthenticated users to the home page
 */
export default function AuthGuard({ children, fallbackPath = '/' }: AuthGuardProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    // Only redirect if we're done loading and user is not authenticated
    if (!isLoading && !isAuthenticated) {
      console.log('🔒 AuthGuard: Redirecting unauthenticated user to', fallbackPath);
      setLocation(fallbackPath);
    }
  }, [isAuthenticated, isLoading, setLocation, fallbackPath]);

  // Show loading while checking authentication
  if (isLoading) {
    return <PageLoader text="Verifying authentication..." />;
  }

  // Don't render protected content if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  // User is authenticated, render the protected content
  return <>{children}</>;
}